import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiRiwayatFaktur, formatApiErrorDetail } from "@/lib/api";
import { Loader2, X } from "lucide-react";
import * as W from "@/lib/waktu";

// v2.13 (owner 2026-10-22, requirement 75 butir 2): the order of payments of ONE invoice —
// shared by Riwayat Pembayaran and the "Riwayat" button of laporan penjualan. Read only.
// "Buka nota" (Owner, Kasir/CS) opens the nota in menu Nota; the owner also gets Pembayaran.
export const rp = (n) => (n < 0 ? "−Rp" : "Rp") + Math.abs(Number(n || 0)).toLocaleString("id-ID");
export const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
export const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
export const CARA = { TUNAI: "Tunai", TRANSFER: "Transfer", QRIS: "QRIS" };
const WARNA = { DP: "bg-amber-500/15 text-amber-700", DP_LANJUTAN: "bg-amber-500/15 text-amber-700", PELUNASAN: "bg-emerald-500/15 text-emerald-700",
  KOREKSI: "bg-sky-500/15 text-sky-700", PENGEMBALIAN: "bg-red-500/15 text-red-700", TIDAK_DIKEMBALIKAN: "bg-slate-500/15 text-slate-600",
  KOREKSI_PENGEMBALIAN: "bg-sky-500/15 text-sky-700" };

export const Jenis = ({ r }) => <span className={`whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-semibold ${WARNA[r.jenis] || ""}`}>{r.jenis_label}</span>;
export const Cara = ({ r }) => (r.cara.length ? r.cara.map((c) => CARA[c]).join(" + ") : "-");
export const Sisa = ({ r }) => (r.sisa_setelah === null || r.sisa_setelah === undefined
  ? <span className="text-xs text-slate-500">saldo kembali {rp(r.saldo_pengembalian_setelah)}</span>
  : <span className={r.sisa_setelah === 0 ? "text-emerald-700 font-semibold" : ""}>{r.sisa_setelah === 0 ? "LUNAS" : rp(r.sisa_setelah)}</span>);


export default function DetailFaktur({ notaId, onClose }) {
  const { user } = useAuth();
  const bukaNota = ["admin", "kasir"].includes(user?.role);
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => { apiRiwayatFaktur(notaId).then(setD).catch((e) => setError(errText(e))); }, [notaId]);
  return (
    <div data-testid="rb-faktur" className="rounded-xl border border-amber-500/40 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-100">Urutan pembayaran {d?.nomor_nota || ""}</h2>
          {d && <p className="text-sm text-slate-400">{d.customer} · total {rp(d.total)} · dibayar {rp(d.dibayar)} · sisa <b>{rp(d.sisa)}</b>{d.status_nota === "CANCELLED" ? " · NOTA BATAL" : ""}</p>}
          {d && bukaNota && (
            <p className="mt-1 flex flex-wrap gap-3 text-xs">
              <Link data-testid="rb-buka-nota" to={`/nota?id=${notaId}`} className="text-soq-biru underline decoration-dotted">Buka nota</Link>
              {user?.role === "admin" && <Link data-testid="rb-buka-pembayaran" to={`/pembayaran?nota=${notaId}`} className="text-soq-biru underline decoration-dotted">Buka di Pembayaran</Link>}
            </p>
          )}
        </div>
        <button data-testid="rb-faktur-tutup" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!d && !error && <Loader2 size={16} className="animate-spin text-slate-400" />}
      {d && (
        <ol data-testid="rb-urutan" className="space-y-2">
          {d.rows.map((r, i) => (
            <li key={r.id} data-testid={`rb-urutan-${i + 1}`} className="grid grid-cols-[2rem_1fr] gap-2 rounded-lg border border-slate-800 p-2.5 text-sm">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-soq-biru text-xs font-bold text-white">{i + 1}</span>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-mono text-xs text-slate-400">{W.waktu(r.tanggal)}</span><Jenis r={r} />
                <span className="font-mono font-semibold">{rp(r.jumlah)}</span><span className="text-xs"><Cara r={r} /></span>
                {r.rekening && <span className="text-xs text-slate-500">{r.rekening}</span>}
                <span className="text-xs">oleh {r.kasir_nama || "-"}</span>
                <span className="text-xs">sisa: <Sisa r={r} /></span>
                {r.catatan && <span className="w-full text-xs text-slate-500">Catatan: {r.catatan}</span>}
              </div>
            </li>
          ))}
          {d.rows.length === 0 && <li className="text-sm text-slate-500">Belum ada pembayaran.</li>}
        </ol>
      )}
    </div>
  );
}
